import { useState, useEffect, useCallback } from 'react';
import './Portfolio.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const campaignImages = [
  '/results/campaign-1.jpeg',
  '/results/campaign-2.jpeg',
];

const fallbackDescription = 'Digital marketing is the use of internet-based channels — such as websites, social media, search engines, and email — to promote products, services, or brands and connect with potential customers.';

// Filter tabs — label shown in UI, value matches "tag" field in DB
const FILTER_TABS = [
  { label: 'ALL PROJECTS', value: 'all' },
  { label: 'BRANDING',     value: 'Branding' },
  { label: 'LOGO',         value: 'Logo' },
  { label: 'SOCIAL MEDIA', value: 'Social Media' },
  { label: 'BROUCHER',     value: 'Broucher' },
  { label: 'WEBSITE',      value: 'Website' },
  { label: 'CAMPAIGN',     value: 'Campaign' },
];

export default function Portfolio({ onOpenCase }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [posters, setPosters]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [lightbox, setLightbox]         = useState(null);

  // ── Fetch from backend ──────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/api/portfolio`)
      .then(r => r.json())
      .then(data => {
        const normalized = data.map(p => ({
          ...p,
          src: p.src.startsWith('/uploads/') ? `${API_BASE}${p.src}` : p.src
        }));
        setPosters(normalized);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ── Filter by active tab then deduplicate by group ──────────────────────────
  const filteredPosters = (() => {
    const filtered = activeFilter === 'all'
      ? posters
      : posters.filter(p => p.tag === activeFilter);

    // Show only first image per group
    const seen = new Set();
    return filtered.filter(p => {
      if (seen.has(p.grp)) return false;
      seen.add(p.grp);
      return true;
    });
  })();

  function openCaseStudy(poster) {
    const group = posters.filter(p => p.grp === poster.grp);
    onOpenCase({
      title:      poster.title,
      category:   `${poster.tag} • ${poster.platform}`,
      images:     group.map(p => p.src),
      scopeTitle: poster.tag.toUpperCase(),
      description: poster.caption || fallbackDescription,
    });
  }

  const prev = useCallback(() => {
    setLightbox(lb => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
  }, []);

  const next = useCallback(() => {
    setLightbox(lb => ({ ...lb, index: (lb.index + 1) % lb.images.length }));
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    function onKey(e) {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     setLightbox(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, prev, next]);

  return (
    <section id="portfolio" className="portfolio-section">

      {/* Header */}
      <div className="portfolio-header reveal">
        <span className="section-label">Our Work</span>
        <h2 className="section-title">Portfolio &amp; <em>Results</em></h2>
        <p className="section-sub">
          Real campaigns, real creatives, real outcomes — powered by Aerial Root Digital.
        </p>
      </div>

      {/* Filter Bar */}
      <div className="portfolio-filter-bar reveal d1">
        {FILTER_TABS.map(f => (
          <button
            key={f.value}
            className={`pfilter-btn ${activeFilter === f.value ? 'active' : ''}`}
            onClick={() => setActiveFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── CAMPAIGN FILTER ── */}
      {activeFilter === 'Campaign' && (
        <div className="results-area">
          <div className="campaign-grid">
            {campaignImages.map((src, i) => (
              <div key={i} className="campaign-single-wrap"
                onClick={() => setLightbox({ images: campaignImages, captions: campaignImages.map(() => ''), index: i })}>
                <img src={src} alt={`Campaign Result ${i + 1}`} className="campaign-single-img" />
                <div className="result-overlay"><span>🔍 View</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── POSTER FILTERS (all/branding/logo/social media/broucher/website) ── */}
      {activeFilter !== 'Campaign' && (
        <>
          {loading ? (
            <div className="portfolio-loading">Loading portfolio...</div>
          ) : filteredPosters.length === 0 ? (
            <div className="portfolio-empty">No projects yet in this category.</div>
          ) : (
            <div className="poster-grid">
              {filteredPosters.map((p) => (
                <div key={p.id} className="poster-card" onClick={() => openCaseStudy(p)}>
                  <div className="poster-img-wrap">
                    <img src={p.src} alt={p.title} className="poster-img" />
                    <div className="poster-hover-overlay">
                      <div className="poster-meta">
                        <span className="poster-tag">{p.tag}</span>
                        <span className="poster-platform">{p.platform}</span>
                      </div>
                      <h4 className="poster-title">{p.title}</h4>
                      {p.caption && <p className="poster-caption">{p.caption}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── LIGHTBOX ── */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={lightbox.images[lightbox.index]} alt="Preview" className="lightbox-img" />
            {lightbox.captions?.[lightbox.index] && (
              <p className="lightbox-caption">{lightbox.captions[lightbox.index]}</p>
            )}
            {lightbox.images.length > 1 && (
              <>
                <button className="lightbox-prev" onClick={prev}>‹</button>
                <button className="lightbox-next" onClick={next}>›</button>
                <div className="lightbox-dots">
                  {lightbox.images.map((_, i) => (
                    <span key={i}
                      className={`lightbox-dot ${i === lightbox.index ? 'active' : ''}`}
                      onClick={() => setLightbox(lb => ({ ...lb, index: i }))}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
